import React from 'react';
import { ArrowRight, ArrowLeft, ExternalLink, Landmark, Sparkles, Megaphone, BookOpen, Handshake, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { logoUrl } from '../data/mock';
import NewsletterSubscribe from '../components/NewsletterSubscribe';

const ZEFFY_URL = 'https://www.zeffy.com/en-US/donation-form/help-build-an-equitable-accessible-and-affordable-early-childhood-system-in-the-district-of-columbia';
const MOMENT_PHOTO = 'https://customer-assets-cm19k8pv.emergentagent.net/job_55671c95-2135-4572-a38e-4ba882651c43/artifacts/xuqaf8uj_unnamed.webp';
const PSI_FLYER = 'https://customer-assets-cm19k8pv.emergentagent.net/job_55671c95-2135-4572-a38e-4ba882651c43/artifacts/8zn6v005_Play_Science_Institute_Flyer.webp';
const WAGES_COVER = '/newsletter/wages-fy26-cover.jpg';

const SectionLabel = ({ children }) => (
  <p className="text-xs font-bold text-amber-600 uppercase tracking-[0.3em] mb-3">{children}</p>
);

const ResourceItem = ({ title, children, href }) => (
  <div className="flex gap-3">
    <ArrowRight className="w-5 h-5 text-teal-600 shrink-0 mt-1" aria-hidden="true" />
    <p className="text-slate-700 leading-relaxed">
      <strong className="text-slate-900">{title}</strong> {children}{' '}
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-700 font-semibold underline hover:text-teal-800 inline-flex items-center gap-1">
        Read more<ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
      </a>
    </p>
  </div>
);

export default function NewsletterPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#faf8f3]">
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Masthead */}
        <div className="bg-gradient-to-br from-[#0a1628] via-[#0f2137] to-[#0f3d3d] pt-32 pb-16 text-center px-4">
          <img src={logoUrl} alt="DC Early Learning Collaborative logo" className="w-20 h-20 mx-auto mb-5 rounded-full bg-white/95 p-1" />
          <p className="text-teal-300 text-xs font-bold uppercase tracking-[0.35em] mb-3">The DC Early Learning Exchange</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="newsletter-title">
            August 2026 Edition
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-base">
            Welcome to the inaugural DC Early Learning Collaborative newsletter!
          </p>
          <button
            onClick={() => navigate('/newsletter')}
            className="mt-6 inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold text-sm underline"
            data-testid="newsletter-back-to-archive"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All Editions
          </button>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">
          {/* Intro */}
          <section className="space-y-4 text-slate-700 leading-relaxed" data-testid="newsletter-intro">
            <p>
              To round up each month, we&#39;ll share a focused look at what is shaping DC&#39;s early childhood landscape,
              why it matters, what DC ELC is doing, and how educators, families, partners, and advocates can stay
              informed and engaged.
            </p>
            <p>
              Our goal is to make this a useful resource for the field, with timely policy updates, opportunities,
              resources, program highlights, and ways to stay connected to the work happening across DC.
            </p>
          </section>

          {/* THE MOMENT */}
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="newsletter-the-moment">
            <img
              src={MOMENT_PHOTO}
              alt="Early childhood leaders in discussion around a conference table at the Stronger Together convening"
              className="w-full h-64 sm:h-80 object-cover"
              data-testid="newsletter-moment-photo"
            />
            <div className="p-8 sm:p-10">
            <SectionLabel>The Moment</SectionLabel>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0a1628] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Future of Head Start
            </h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                For more than 60 years, Head Start has represented a national commitment to children and families,
                combining early learning with health, family engagement, nutrition, and other supports designed to help
                children thrive.
              </p>
              <p className="font-semibold text-slate-900">
                This month, the future of how that commitment is carried out is up for debate.
              </p>
              <p>
                On August 7, the federal Administration for Children and Families released a proposed rule that would
                significantly rewrite the Head Start Program Performance Standards. The Administration describes the
                proposal as an effort to reduce administrative burden, give programs greater flexibility, and allow more
                resources to reach children and families.
              </p>
              <p>
                At the same time, the changes raise important questions for the early childhood field about what happens
                when longstanding federal standards for quality, workforce, family engagement, and program operations are
                significantly reduced or shifted to states and local programs.
              </p>
              <p>
                This month, we are focused on what the proposed Head Start changes could mean for children, families,
                educators, and programs, and why the early childhood field should be paying attention now.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6">
              <div className="border-l-4 border-teal-600 bg-teal-50/60 rounded-r-xl p-5">
                <h3 className="text-sm font-bold text-teal-800 uppercase tracking-wider mb-2">Why This Matters</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  For DC, this conversation extends beyond Head Start alone. Our early childhood system depends on quality
                  standards, a prepared and supported workforce, strong family partnerships, and sustainable programs. As
                  federal expectations change, we must ask what flexibility can improve and which protections are
                  essential to preserving the quality and equity children and families deserve.
                </p>
              </div>
              <div className="border-l-4 border-amber-500 bg-amber-50/60 rounded-r-xl p-5">
                <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-2">What DC ELC Is Doing</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  DC ELC is reviewing the proposed changes and considering their implications for DC&#39;s early childhood
                  community. We will continue sharing accessible information with educators, program leaders, families,
                  and partners while creating opportunities for the field to understand policy changes and make their
                  voices heard.
                </p>
                <p className="text-sm text-slate-700 leading-relaxed mt-2">
                  This work is also central to Stronger Together, our growing field-building initiative focused on
                  ensuring early childhood leaders are informed, connected, and equipped to respond collectively to the
                  issues shaping our system.
                </p>
              </div>
              <div className="border-l-4 border-[#0a1628] bg-slate-50 rounded-r-xl p-5">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-2">What You Can Do</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Review the proposed changes. Talk with your colleagues about what they could mean in practice. Share
                  your perspective during the federal public-comment period.
                </p>
              </div>
            </div>
            </div>
          </section>

          {/* AROUND THE COLLABORATIVE */}
          <section data-testid="newsletter-around">
            <SectionLabel>Around the Collaborative</SectionLabel>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 items-start">
                  <a href={PSI_FLYER} target="_blank" rel="noopener noreferrer" aria-label="View the full Play Science Institute flyer (opens in a new tab)">
                    <img
                      src={PSI_FLYER}
                      alt="Play Science Institute flyer — Learn. Play. Transform. Saturday, September 19, 2026, 8 AM to 5 PM, 700 Pennsylvania Ave SE, Washington DC"
                      className="w-full rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                      data-testid="newsletter-psi-flyer"
                    />
                  </a>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-teal-600" aria-hidden="true" />
                      <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Play Science Institute</h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-3">
                      Join DC ELC and D. Jones Consultant, LLC for a full-day professional learning experience for
                      community-based program directors, lead teachers, and early childhood educators.
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mb-4">→ Saturday, September 19, 2026 | 8:00 AM–5:00 PM</p>
                    <Button
                      onClick={() => navigate('/professional-development/play-science-institute/register')}
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                      data-testid="newsletter-psi-register"
                    >
                      Register here by September 5th
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
                <div className="flex items-center gap-2 mb-2">
                  <Landmark className="w-5 h-5 text-teal-600" aria-hidden="true" />
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Maurice Sykes Emerging Leaders Program</h3>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Our inaugural cohort continues its leadership development journey through learning, reflection,
                  mentorship, and opportunities to apply new leadership skills in practice. We look forward to sharing
                  more from our emerging leaders throughout the fall.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="w-5 h-5 text-teal-600" aria-hidden="true" />
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Educator Advocacy Training</h3>
                </div>
                <p className="text-slate-700 leading-relaxed mb-4">
                  New advocacy learning opportunities are coming this fall to help early childhood educators better
                  understand the policy process, connect their experiences to policy decisions, and strengthen their
                  ability to advocate for themselves, their colleagues, children, and families.
                </p>
                <button
                  onClick={() => navigate('/community')}
                  className="text-teal-700 font-semibold underline hover:text-teal-800 inline-flex items-center gap-1"
                  data-testid="newsletter-fall-calendar"
                >
                  View our fall calendar <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>

          {/* STRONGER TOGETHER + VIDEO */}
          <section className="bg-[#0a1628] rounded-2xl p-8 sm:p-10 text-white" data-testid="newsletter-stronger-together">
            <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.3em] mb-3">New Initiative</p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Stronger Together
            </h2>
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>
                In May, DC ELC brought together early childhood association leaders, educators, program leaders, and
                advocates for <em className="text-white">Stronger Together: Protecting Progress Through Collective
                Leadership</em>. The convening created space to reflect on what is happening across DC&#39;s early
                childhood system, identify gaps in leadership and advocacy infrastructure, and discuss what stronger
                coordination across the field could look like. Watch the recap below!
              </p>
            </div>

            <div className="my-7 rounded-xl overflow-hidden border border-white/15 aspect-video">
              <iframe
                src="https://www.youtube.com/embed/OhBEWiB0xpU"
                title="Stronger Together: Protecting Progress Through Collective Leadership — convening recap video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="newsletter-recap-video"
              />
            </div>

            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>
                Across the day, several themes came through clearly: the need for stronger shared messaging, sustained
                investment in the workforce, better coordination across organizations, more opportunities for leadership
                and advocacy development, and meaningful educator and family voice in decision-making.
              </p>
              <p>
                This fall, Stronger Together will continue as an ongoing field-building and leadership initiative focused
                on connecting, equipping, and aligning early childhood educators, program leaders, associations, and
                advocates. Upcoming work will include advocacy training, peer learning, future convenings, shared
                resources, and opportunities for coordinated action around emerging policy and budget issues.
              </p>
            </div>
            <Button
              onClick={() => {
                navigate('/');
                setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 500);
              }}
              className="mt-6 bg-amber-500 hover:bg-amber-600 text-[#0a1628] font-bold"
              data-testid="newsletter-st-signup"
            >
              Sign up to learn more about Stronger Together
            </Button>
          </section>

          {/* FEATURED OPPORTUNITIES */}
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10" data-testid="newsletter-resources">
            <SectionLabel>Featured Opportunities and Resources</SectionLabel>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-5 items-start">
                <img
                  src={WAGES_COVER}
                  alt="Child Care WAGE$ National Impact FY 2026 report cover"
                  className="w-full max-w-[150px] rounded-lg border border-slate-200 shadow-sm"
                  data-testid="newsletter-wages-cover"
                />
                <ResourceItem
                  title="Child Care WAGE$® FY26 Impact:"
                  href="https://www.teachecnationalcenter.org/child-care-wages/"
                >
                  Early Years reports that the WAGE$ program invested more than $25 million in salary supplements for 9,362
                  early educators across five states, with participating programs reporting a 14% turnover rate. A useful
                  resource as DC continues conversations about compensation, retention, and the value of investing directly
                  in the ECE workforce.
                </ResourceItem>
              </div>
              <ResourceItem
                title="Which DC Families Opt Out of Universal Preschool?"
                href="https://www.urban.org/research/publication/which-families-opt-out-universal-preschool"
              >
                A new Urban Institute brief examines which DC families do not apply for or enroll in universal preschool
                and what their experiences can teach policymakers about access and family needs.
              </ResourceItem>
              <ResourceItem
                title="New Pay Equity Fund Guidance from OSSE:"
                href="https://osse.dc.gov/publication/early-childhood-educator-pay-equity-fund-how-staffing-changes-impact-quarterly-awards"
              >
                OSSE released updated guidance on August 20 explaining how staffing changes can affect Pay Equity Fund
                quarterly awards and participating facilities&#39; salary requirements. The resource is available in
                English, Spanish, and Amharic.
              </ResourceItem>
            </div>
          </section>

          {/* CTA row */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="newsletter-ctas">
            <Button onClick={() => navigate('/partner-with-us')} className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white py-6">
              <Handshake className="w-4 h-4 mr-2" aria-hidden="true" /> Partner With Us
            </Button>
            <Button onClick={() => {
              navigate('/');
              setTimeout(() => document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' }), 500);
            }} variant="outline" className="border-slate-300 text-slate-800 hover:bg-slate-50 py-6">
              <BookOpen className="w-4 h-4 mr-2" aria-hidden="true" /> Learn About Our Programs
            </Button>
            <a href={ZEFFY_URL} target="_blank" rel="noopener noreferrer" aria-label="Donate to DC Early Learning Collaborative (opens in a new tab)"
              className="inline-flex items-center justify-center rounded-lg bg-[#079897] hover:bg-[#067a79] text-white font-semibold py-4 transition-colors">
              <Heart className="w-4 h-4 mr-2" aria-hidden="true" /> Donate
            </a>
          </section>

          <NewsletterSubscribe />

          <p className="text-center text-sm text-slate-500 italic">
            DC Early Learning Collaborative exists to strengthen the early childhood system for every child, family, and
            educator in the District.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
